import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { url } from '../../../../../env';
import { DataForType } from './DataForType';
import { StepVisitDetails } from './OcularVisitSteps/StepVisitDetails';
import { StepCompanyEmail } from './OcularVisitSteps/StepCompanyEmail';
import { StepUserEmail } from './OcularVisitSteps/StepUserEmail';
import { Button } from 'primereact/button';


export const OcularVisitInline = ({ persona, stepStatus, stepData, onSuccess, toast, processId }) => {
    const isCompleted = stepStatus === 'completed';
    const isUnsuccessful = stepStatus === 'unsuccessful';
    const [loading, setLoading] = useState(false);

    // Default attachments (onboarding) — se cargan vía endpoint dedicado para que
    // funcionen aunque el step ocular_visit aún no exista en BD.
    const [defaultAttachments, setDefaultAttachments] = useState([]);
    // Set de s3_path que el admin removió desde el UI (no se enviarán al correo)
    const [excludedDefaultPaths, setExcludedDefaultPaths] = useState([]);

    useEffect(() => {
        const fetchDefaultAttachments = async () => {
            if (!persona?.id) return;
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${url}/quote-process/ocular-default-attachments/${persona.id}`, {
                    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
                });
                setDefaultAttachments(res.data?.data?.default_attachments || []);
            } catch (err) {
                setDefaultAttachments([]);
            }
        };
        fetchDefaultAttachments();
    }, [persona?.id]);

    // New states for email and CC
    const [email, setEmail] = useState('');
    const [ccEmails, setCcEmails] = useState([]);
    const [emailContent, setEmailContent] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [currentStatus, setCurrentStatus] = useState('scheduled'); // pending, scheduled, postponed, paused, completed
    const [completionOutcome, setCompletionOutcome] = useState(null); // 'successful' | 'unsuccessful' | null
    const [adminComment, setAdminComment] = useState('');

    // Step 2 (Company Email) states
    const [companyRecipientName, setCompanyRecipientName] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [companyCcEmails, setCompanyCcEmails] = useState([]);
    const [companyEmailSubject, setCompanyEmailSubject] = useState('');
    const [companyEmailContent, setCompanyEmailContent] = useState('');
    const [companyUploadedFiles, setCompanyUploadedFiles] = useState([]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(1);

    const navigate = (nextIndex) => {
        setDirection(nextIndex > activeIndex ? 1 : -1);
        setActiveIndex(nextIndex);
    };

    // Compute the resolved status that will be sent to backend
    const resolvedStatus = React.useMemo(() => {
        if (currentStatus === 'completed') {
            if (completionOutcome === 'unsuccessful') return 'unsuccessful';
            if (completionOutcome === 'successful') return 'completed';
            return 'completed'; // default if no outcome selected yet
        }
        return currentStatus;
    }, [currentStatus, completionOutcome]);

    // Días desde la última actualización del step (para aging color y leyenda)
    const daysSinceUpdate = React.useMemo(() => {
        const ref = stepData?.updated_at || stepData?.completed_at;
        if (!ref) return null;
        const diffMs = Date.now() - new Date(ref).getTime();
        if (Number.isNaN(diffMs) || diffMs < 0) return null;
        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }, [stepData?.updated_at, stepData?.completed_at]);

    // Badge dinámico de estado — paleta HSL premium por estado/outcome
    // + aging color: estados en espera (scheduled/postponed/paused) cambian a
    //   ámbar (3-7 días) o rojo (>7 días) para empujar al admin a actuar.
    const badgeDetails = React.useMemo(() => {
        // Estado efectivo (mapea outcome cuando es completed)
        let key = currentStatus;
        if (currentStatus === 'completed') {
            key = completionOutcome === 'unsuccessful' ? 'unsuccessful' : 'successful';
        }

        const map = {
            pending: {
                label: 'Pendiente',
                icon: 'pi pi-clock',
                hue: 38,        // ámbar/naranja
            },
            scheduled: {
                label: 'Programada',
                icon: 'pi pi-calendar-plus',
                hue: 212,       // azul profesional
            },
            postponed: {
                label: 'Reprogramada',
                icon: 'pi pi-history',
                hue: 268,       // púrpura elegante
            },
            paused: {
                label: 'Pausada',
                icon: 'pi pi-pause-circle',
                hue: 220,       // gris-azulado neutro
                saturation: 8,
            },
            successful: {
                label: 'Exitosa',
                icon: 'pi pi-check-circle',
                hue: 152,       // verde esmeralda
            },
            unsuccessful: {
                label: 'No Exitosa',
                icon: 'pi pi-times-circle',
                hue: 4,         // rojo suave
            },
        };

        const cfg = map[key] || map.pending;
        let hue = cfg.hue;
        let sat = cfg.saturation ?? 78;
        let agingTier = 'fresh'; // fresh | warning | urgent

        const isWaiting = ['scheduled', 'postponed', 'paused'].includes(key);
        if (isWaiting && daysSinceUpdate !== null) {
            if (daysSinceUpdate > 7) {
                hue = 4;   // rojo
                sat = 78;
                agingTier = 'urgent';
            } else if (daysSinceUpdate >= 3) {
                hue = 38;  // ámbar
                sat = 88;
                agingTier = 'warning';
            }
        }

        return {
            label: cfg.label,
            icon: cfg.icon,
            agingTier,
            bg: `hsl(${hue}, ${sat}%, 96%)`,
            border: `hsl(${hue}, ${sat}%, 82%)`,
            text: `hsl(${hue}, ${Math.max(40, sat - 10)}%, 32%)`,
            dot: `hsl(${hue}, ${sat}%, 48%)`,
            shadow: `hsl(${hue}, ${sat}%, 70%)`,
        };
    }, [currentStatus, completionOutcome, daysSinceUpdate]);

    // Estado realmente persistido en BD — el tracker y el hint se manejan por esto,
    // NO por currentStatus (que es el draft del dropdown). Antes, defaultear el
    // dropdown a "scheduled" hacía que el tracker mintiera mostrando "en proceso"
    // antes de que el admin guardara.
    const savedStatus = stepData?.status || 'pending';

    // Texto del "siguiente paso" — guía contextual basada en el estado guardado.
    const nextStepHint = React.useMemo(() => {
        const days = daysSinceUpdate;
        const ago = days === null ? '' : days === 0 ? 'hoy' : days === 1 ? 'hace 1 día' : `hace ${days} días`;
        if (savedStatus === 'pending') return 'Aún no se programa — completa los datos y envía el correo de programación.';
        if (savedStatus === 'scheduled') return `Programada ${ago} · Marca como Completada cuando MANUS confirme.`;
        if (savedStatus === 'postponed') return `Reprogramada ${ago} · Confirma la nueva fecha y marca como Completada.`;
        if (savedStatus === 'paused') return `Pausada ${ago} · Reanuda cuando el cliente esté disponible.`;
        if (savedStatus === 'completed') return 'Visita completada con éxito · Listo para Confirmación de Cotización.';
        if (savedStatus === 'unsuccessful') return 'Resultado no exitoso · El trámite no puede avanzar por ahora.';
        return null;
    }, [savedStatus, daysSinceUpdate]);

    // Tracker de progreso de la visita (lineal: Pendiente → En proceso → Completada).
    // Refleja el estado guardado en BD, no el borrador del formulario.
    const progressTracker = React.useMemo(() => {
        let activeIdx;
        let lastStageVariant; // 'success' | 'failure' | null
        if (savedStatus === 'pending') { activeIdx = 0; lastStageVariant = null; }
        else if (['scheduled', 'postponed', 'paused'].includes(savedStatus)) { activeIdx = 1; lastStageVariant = null; }
        else if (savedStatus === 'completed') { activeIdx = 2; lastStageVariant = 'success'; }
        else if (savedStatus === 'unsuccessful') { activeIdx = 2; lastStageVariant = 'failure'; }
        else { activeIdx = 0; lastStageVariant = null; }

        const stages = [
            { key: 'pending', label: 'Pendiente', icon: 'pi pi-clock' },
            { key: 'scheduled', label: 'En proceso', icon: 'pi pi-spinner' },
            { key: 'completed', label: lastStageVariant === 'failure' ? 'No exitosa' : 'Completada', icon: lastStageVariant === 'failure' ? 'pi pi-times-circle' : 'pi pi-check-circle' },
        ];
        return { stages, activeIdx, lastStageVariant };
    }, [savedStatus]);

    // Whether the wizard should skip the company email step (2-step mode)
    // Skip when status is 'completed' (regardless of outcome) — only user gets notified
    const skipCompanyEmail = currentStatus === 'completed';

    // Filter status options based on current status rules
    const statusOptions = React.useMemo(() => {
        const allOptions = [
            { label: 'Pendiente', value: 'pending' },
            { label: 'Programada (Enviar Correo)', value: 'scheduled' },
            { label: 'Reprogramada (Posponer)', value: 'postponed' },
            { label: 'Pausada', value: 'paused' },
            { label: 'Completada', value: 'completed' }
        ];

        const initialStatus = stepData?.status;
        const metadata = stepData?.metadata;
        const hasVisitData = metadata?.visit_data && (metadata.visit_data.full_name || Object.keys(metadata.visit_data).length > 0);

        if (initialStatus === 'postponed') {
            return allOptions.filter(opt => !['pending', 'scheduled'].includes(opt.value));
        }

        if (initialStatus === 'paused' && hasVisitData) {
            return allOptions.filter(opt => !['pending', 'scheduled'].includes(opt.value));
        }

        if (initialStatus === 'completed') {
            // Completed is immutable
            return allOptions.filter(opt => opt.value === 'completed');
        }

        if (initialStatus === 'unsuccessful') {
            // Can go to: scheduled, postponed, paused, completed (which includes unsuccessful via outcome)
            return allOptions.filter(opt => ['scheduled', 'postponed', 'paused', 'completed'].includes(opt.value));
        }

        return allOptions;
    }, [stepData]);

    // Helper to generate default content
    const generateEmailContent = (status, currentVisitsData, selectedTypes, name) => {

        let visitsDetails = '';
        if (selectedTypes && selectedTypes.length > 0 && currentVisitsData) {
            visitsDetails = selectedTypes.map(type => {
                const data = currentVisitsData[type];
                if (!data) return '';
                // tentativeDate removed — MANUS coordina la fecha directamente
                // let dateStr = 'Fecha por definir';
                // if (data.tentativeDate) { ... }

                const addressParts = [
                    data.address,
                    data.colonia,
                    data.municipality,
                    data.postalCode ? `CP ${data.postalCode}` : ''
                ].filter(part => part && part.trim() !== '');

                const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : 'N/A';

                return `🔹 *${type}*\n📍 Dirección: ${fullAddress}\n👤 Recibe: ${data.recipientName || 'N/A'}\n📞 Teléfono: ${data.phoneNumber || 'N/A'}\n📅 MANUS se pondrá en contacto para coordinar la fecha y hora de la visita.`;
            }).join('\n\n');
        }

        switch (status) {
            case 'scheduled':
                return `¡Hola ${name}! 🌟\n\nNos complace informarte que hemos iniciado el proceso de tu(s) visita(s) ocular(es). 🗓️\n\n${visitsDetails}\n\nPor favor, asegúrate de que quien recibe esté disponible para recibir la llamada de coordinación. Estamos ansiosos por dar el siguiente paso contigo. 🚗💨\n\nPuedes seguir el estatus de tu solicitud desde tu dashboard.\n\nSaludos,\nEquipo Peicar`;

            case 'postponed':
                return `Hola ${name}, 👋\n\nTu visita ocular ha sido reprogramada. 🗓️\n\n${visitsDetails}\n\nLamentamos los inconvenientes.\n\nPuedes seguir el estatus de tu solicitud desde tu dashboard.\n\nSaludos,\nEquipo Peicar`;

            case 'paused':
                return `Hola ${name}, 👋\n\nHemos pausado tu proceso de visita ocular temporalmente. ⏸️\nUn administrador se pondrá en contacto contigo muy pronto para reanudarlo. 📞\n\nGracias por tu paciencia. ¡Seguimos avanzando! 🚀\n\nSaludos,\nEquipo Peicar`;

            case 'completed':
                return `¡Excelente noticia, ${name}! 🎉\n\nLa visita ocular se ha realizado correctamente. ✅\n\nTu proceso avanza al siguiente paso: Confirmación de Cotización. 📝✨\n\n¡Gracias por tu confianza!\n\nPuedes seguir el estatus de tu solicitud desde tu dashboard.\n\nSaludos,\nEquipo Peicar`;

            case 'unsuccessful':
                return `Hola ${name}, 👋\n\nTe informamos que, tras la revisión de tu visita ocular, lamentablemente el resultado no fue favorable en esta ocasión. 😔\n\nEsto significa que tu trámite no puede avanzar por el momento. Un asesor se comunicará contigo si hay novedades o si es posible reprogramar el proceso.\n\nAgradecemos tu comprensión y paciencia. 🙏\n\nSaludos cordiales,\nEquipo Peicar`;

            default:
                return '';
        }
    };

    const generateEmailSubject = (status) => {
        switch (status) {
            case 'scheduled': return 'Visita Ocular Programada';
            case 'postponed': return 'Visita Ocular Reprogramada';
            case 'paused': return 'Visita Ocular Pausada';
            case 'completed': return 'Visita Ocular Completada con Éxito';
            case 'unsuccessful': return 'Resultado de tu Visita Ocular';
            default: return 'Actualización de tu Visita Ocular';
        }
    };

    const generateCompanyEmailSubject = (isPM) => {
        const type = isPM ? 'Persona Moral' : 'Persona Física';
        return `Coordinación de visita ocular – ${type} | PEICAR`;
    };

    const generateCompanyEmailContent = (status, visitsData, selectedTypes, persona, isPM, recipientName) => {
        const typeStr = isPM ? 'persona moral' : 'persona física';
        let visitsDetails = '';

        if (selectedTypes && selectedTypes.length > 0 && visitsData) {
            visitsDetails = selectedTypes.map(type => {
                const data = visitsData[type];

                if (!data) return '';

                // tentativeDate removed — MANUS coordina la fecha directamente
                // let dateStr = 'Fecha por definir';
                // if (data.tentativeDate) { ... }

                const addressPartsCompany = [
                    data.address,
                    data.colonia,
                    data.municipality,
                    data.postalCode ? `CP ${data.postalCode}` : ''
                ].filter(part => part && part.trim() !== '');

                const fullAddressCompany = addressPartsCompany.length > 0 ? addressPartsCompany.join(', ') : 'N/A';

                return `🔹 *${type}*\n📍 Dirección: ${fullAddressCompany}\n👤 Recibe: ${data.recipientName || 'N/A'}\n📞 Teléfono: ${data.phoneNumber || 'N/A'}`;
            }).join('\n\n');
        }

        const greeting = recipientName ? `Estimado(a) ${recipientName},` : 'Estimados,';
        let baseIntro = `${greeting}\n\nBuen día ☀️.\nComo parte del proceso de arrendamiento con PEICAR, solicitamos su apoyo referente a la(s) visita(s) ocular(es) para el siguiente cliente ${typeStr}.\n\n📋 Datos del cliente:\n- Nombre del cliente: ${persona?.name || 'N/A'}\n- Email: ${persona?.email || 'N/A'}\n- Teléfono: ${persona?.phone || 'N/A'}\n\n`;

        // Solo agregar los detalles de las visitas si es programada o reprogramada
        if (['scheduled', 'postponed'].includes(status)) {
            baseIntro += `📍 Visitas Oculares solicitadas:\n${visitsDetails}\n\n`;
        }

        switch (status) {
            case 'scheduled':
                return `${baseIntro}Les pedimos que contacten directamente al cliente para coordinar fecha y horario de la(s) visita(s) o confirmen la información arriba mencionada. 📆\n\nQuedamos atentos a su confirmación. ✅\n\nSaludos cordiales,\nEquipo PEICAR 🚗`;
            case 'postponed':
                return `${baseIntro}Les informamos que la(s) visita(s) ha(n) sido reprogramada(s). 🔄 Favor de tomar nota de las nuevas fechas/detalles y contactar al cliente de ser necesario.\n\nQuedamos atentos a sus comentarios.\n\nSaludos cordiales,\nEquipo PEICAR 🚗`;
            case 'paused':
                return `${baseIntro}Les notificamos que el proceso de visita(s) ocular(es) para este cliente se encuentra temporalmente PAUSADO. ⏸️\n\nLes solicitamos detener sus actividades relacionadas hasta nuevo aviso.\n\nSaludos cordiales,\nEquipo PEICAR 🚗`;
            case 'completed':
                return `${baseIntro}Les informamos que la(s) visita(s) ocular(es) ha(n) sido marcadas como COMPLETADAS exitosamente. 🎉✅\n\nAgradecemos su apoyo durante este proceso. El trámite continuará a la siguiente etapa de Confirmación de Cotización.\n\nSaludos cordiales,\nEquipo PEICAR 🚗`;
            default:
                return `${baseIntro}Les pedimos estar al tanto sobre este proceso.\n\nSaludos cordiales,\nEquipo PEICAR 🚗`;
        }
    };

    // Visit Types Logic
    const isPM = persona?.tipo_persona === 'PM';
    /*  const isPM = true; */
    const visitTypeOptions = isPM
        ? [
            { label: 'Visita Operativa', value: 'Visita Operativa' },
            { label: 'Visita Domicilio Particular', value: 'Visita Domicilio Particular' },
            { label: 'Visita Fiscal', value: 'Visita Fiscal' }
        ]
        : [{ label: 'Visita Particular', value: 'Visita Particular' }];

    const [selectedVisitTypes, setSelectedVisitTypes] = useState([]);
    const [visitsData, setVisitsData] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    // Auto-fill municipality helper
    const fetchAddressData = async (zip, type) => {
        if (zip && zip.length === 5) {
            try {
                const response = await axios.get(`${url}/getAddresByZipCode/${zip}`);
                if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
                    const data = response.data.data;
                    const options = data.map(item => ({ label: item.colonia, value: item.colonia }));

                    setVisitsData(prev => {
                        const currentData = prev[type] || {};
                        const newColonia = options.length === 1 ? options[0].value : (currentData.colonia || '');

                        return {
                            ...prev,
                            [type]: {
                                ...currentData,
                                municipality: data[0].municipio,
                                coloniasOptions: options,
                                colonia: newColonia
                            }
                        };
                    });
                }
            } catch (error) {
                console.error("Error fetching address:", error);
            }
        }
    };

    // Update specific visit data
    const updateVisitData = (type, field, value) => {
        setVisitsData(prev => {
            const currentData = prev[type] || {};
            const newData = { ...currentData, [field]: value };

            const nextState = { ...prev, [type]: newData };

            if (currentStatus !== 'pending') {
                setEmailContent(generateEmailContent(currentStatus, nextState, selectedVisitTypes, persona?.name || 'Usuario'));
            }

            // Clear validation error if field is filled
            if (value && validationErrors[type]?.[field]) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    if (newErrors[type]) {
                        const newTypeErrors = { ...newErrors[type] };
                        delete newTypeErrors[field];
                        if (Object.keys(newTypeErrors).length === 0) {
                            delete newErrors[type];
                        } else {
                            newErrors[type] = newTypeErrors;
                        }
                    }
                    return newErrors;
                });
            }

            return nextState;
        });

        // Trigger address lookup if postal code changes
        if (field === 'postalCode' && value.length === 5) {
            fetchAddressData(value, type);
        }
    };

    useEffect(() => {
        let currentVisitsData = {};
        let currentSelectedTypes = [];

        const data = stepData?.metadata?.visit_data;
        const savedSubject = stepData?.metadata?.email_subject;
        const savedContent = stepData?.metadata?.email_content;

        if (data) {
            if (data.full_name) {
                // Legacy flat structure
                const initialTypeArray = ['Visita Particular'];
                currentSelectedTypes = initialTypeArray;
                currentVisitsData = {
                    [initialTypeArray[0]]: {
                        fullName: getFullName(data.full_name),
                        address: data.address || '',
                        postalCode: data.postal_code || '',
                        municipality: data.municipality || '',
                        colonia: data.colonia || '',
                        visitsCount: data.visits_count || 1,
                        tentativeDate: data.tentative_date ? new Date(data.tentative_date) : null,
                        recipientName: '',
                        phoneNumber: ''
                    }
                };
                if (data.postal_code) fetchAddressData(data.postal_code, initialTypeArray[0]);
                setAdminComment(data.admin_comment || '');
                setEmail(data.email_sent_to || persona?.email || '');
                setCcEmails(data.cc_emails || []);
            } else {
                // New structure (keyed by type)
                const types = Object.keys(data).filter(k => k !== 'email_sent_to' && k !== 'cc_emails' && k !== 'admin_comment' && k !== 'email_content' && k !== 'email_subject');
                currentSelectedTypes = types;

                if (types.length > 0) {
                    currentVisitsData = { ...data };
                    types.forEach(t => {
                        if (currentVisitsData[t].tentativeDate) {
                            currentVisitsData[t].tentativeDate = new Date(currentVisitsData[t].tentativeDate);
                        }
                        if (currentVisitsData[t].postalCode) {
                            fetchAddressData(currentVisitsData[t].postalCode, t);
                        }
                        if (!currentVisitsData[t].fullName) {
                            currentVisitsData[t].fullName = getFullName('');
                        }
                    });
                } else {
                    currentSelectedTypes = isPM ? [] : ['Visita Particular'];
                }
                setAdminComment(stepData?.metadata?.admin_comment || '');
                setEmail(stepData?.metadata?.email_sent_to || persona?.email || '');
                setCcEmails(stepData?.metadata?.cc_emails || []);
            }
        } else {
            // Default new
            currentSelectedTypes = isPM ? [] : ['Visita Particular'];
            if (!isPM) {
                const defType = 'Visita Particular';
                const budgetZip = persona?.infoBudget?.zip || '';
                currentVisitsData = {
                    [defType]: {
                        fullName: getFullName(''),
                        address: '', // calle y número: no se tiene, el admin lo captura
                        postalCode: budgetZip,
                        municipality: persona?.infoBudget?.city || '',
                        colonia: persona?.infoBudget?.colony || '',
                        visitsCount: 1,
                        tentativeDate: null,
                        recipientName: persona?.name || '',
                        phoneNumber: persona?.phone ? String(persona.phone) : ''
                    }
                };
                if (budgetZip) fetchAddressData(budgetZip, defType);
            }
            setAdminComment('');
            setEmail(persona?.email || '');
            setCcEmails([]);
            setCompanyRecipientName('');
            setCompanyEmail('');
            setCompanyCcEmails([]);
        }

        // Sync states
        setVisitsData(currentVisitsData);
        setSelectedVisitTypes(currentSelectedTypes);

        // Set current status from stepData. Sin datos guardados → 'pending'
        // (antes defaulteaba a 'scheduled' lo cual confundía al admin haciéndolo
        // creer que la visita ya estaba programada).
        const initialStatusStr = stepData?.status || 'pending';
        let effectiveStatus;
        if (initialStatusStr === 'unsuccessful') {
            // Show dropdown as 'Completada' with outcome 'No exitosa'
            effectiveStatus = 'completed';
            setCompletionOutcome('unsuccessful');
        } else if (initialStatusStr === 'completed') {
            effectiveStatus = 'completed';
            setCompletionOutcome('successful');
        } else {
            effectiveStatus = initialStatusStr;
            setCompletionOutcome(null);
        }
        setCurrentStatus(effectiveStatus);

        // Populate email fields: priority to metadata, then templates
        if (savedSubject) {
            setEmailSubject(savedSubject);
        } else if (effectiveStatus !== 'pending') {
            setEmailSubject(generateEmailSubject(effectiveStatus));
        }

        if (savedContent) {
            setEmailContent(savedContent);
        } else if (effectiveStatus !== 'pending') {
            setEmailContent(generateEmailContent(effectiveStatus, currentVisitsData, currentSelectedTypes, persona?.name || 'Usuario'));
        }

        // Step 2 Initialization (load from metadata if exists)
        const initialCompanyRecipient = stepData?.metadata?.company_recipient_name || '';
        setCompanyRecipientName(initialCompanyRecipient);
        setCompanyEmail(stepData?.metadata?.company_email || '');
        setCompanyCcEmails(stepData?.metadata?.company_cc_emails || []);
        setCompanyEmailSubject(stepData?.metadata?.company_email_subject || generateCompanyEmailSubject(isPM));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepData, persona, isPM]);

    // Default company content generator combined with stepData loading
    useEffect(() => {
        if (persona) {
            if (isCompleted && stepData?.metadata) {
                const metadata = stepData.metadata;
                setCompanyEmailSubject(metadata.company_email_subject || generateCompanyEmailSubject(isPM));
                setCompanyEmailContent(metadata.company_email_content || generateCompanyEmailContent(currentStatus, visitsData, selectedVisitTypes, persona, isPM, companyRecipientName));
            } else {
                setCompanyEmailSubject(generateCompanyEmailSubject(isPM));
                setCompanyEmailContent(generateCompanyEmailContent(currentStatus, visitsData, selectedVisitTypes, persona, isPM, companyRecipientName));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persona, isPM, visitsData, selectedVisitTypes, companyRecipientName, isCompleted, stepData, currentStatus]);

    // Helper to get formatted name
    const getFullName = (initial) => initial || persona?.name || '';

    const handleVisitTypeChange = (e) => {
        const selected = e.value;
        setSelectedVisitTypes(selected);

        // Clear validation error if at least one is selected
        if (selected.length > 0 && validationErrors.selectedVisitTypes) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.selectedVisitTypes;
                return newErrors;
            });
        }

        // Initialize data for new types
        setVisitsData(prev => {
            const next = { ...prev };
            selected.forEach(type => {
                if (!next[type]) {
                    next[type] = {
                        visitsCount: 1,
                        fullName: getFullName(''),
                        address: '',
                        postalCode: '',
                        municipality: '',
                        colonia: '',
                        recipientName: '',
                        phoneNumber: '',
                        coloniasOptions: []
                    };
                }
            });
            return next;
        });
    };

    const handleStatusChange = (e) => {
        const newStatus = e.value;
        setCurrentStatus(newStatus);

        // Reset completion outcome when status changes away from 'completed'
        if (newStatus !== 'completed') {
            setCompletionOutcome(null);
        }

        if (newStatus !== 'pending') {
            setEmailContent(generateEmailContent(newStatus, visitsData, selectedVisitTypes, persona?.name || 'Usuario'));
            setEmailSubject(generateEmailSubject(newStatus));
            setCompanyEmailSubject(generateCompanyEmailSubject(isPM));
            setCompanyEmailContent(generateCompanyEmailContent(newStatus, visitsData, selectedVisitTypes, persona, isPM, companyRecipientName));
        } else {
            setEmailContent('');
            setEmailSubject('');
            setCompanyEmailContent('');
            setCompanyEmailSubject('');
        }
    };

    // Handler for completion outcome change (successful/unsuccessful)
    const handleCompletionOutcomeChange = (outcome) => {
        setCompletionOutcome(outcome);
        const emailStatus = outcome === 'unsuccessful' ? 'unsuccessful' : 'completed';
        setEmailContent(generateEmailContent(emailStatus, visitsData, selectedVisitTypes, persona?.name || 'Usuario'));
        setEmailSubject(generateEmailSubject(emailStatus));
        if (outcome === 'successful') {
            setCompanyEmailSubject(generateCompanyEmailSubject(isPM));
            setCompanyEmailContent(generateCompanyEmailContent('completed', visitsData, selectedVisitTypes, persona, isPM, companyRecipientName));
        }
    };

    // CC Handlers
    const addCcEmail = () => setCcEmails([...ccEmails, '']);
    const updateCcEmail = (index, value) => {
        const newCc = [...ccEmails];
        newCc[index] = value;
        setCcEmails(newCc);
    };
    const removeCcEmail = (index) => setCcEmails(ccEmails.filter((_, i) => i !== index));

    const validateVisits = () => {
        const newErrors = {};
        let hasErrors = false;

        if (selectedVisitTypes.length === 0) {
            newErrors.selectedVisitTypes = true;
            setValidationErrors(newErrors);
            toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Debes seleccionar al menos un tipo de visita.' });
            return false;
        }

        selectedVisitTypes.forEach(type => {
            const d = visitsData[type] || {};
            const typeErrors = {};

            if (!d.fullName || !d.fullName.trim()) typeErrors.fullName = true;
            if (!d.recipientName || !d.recipientName.trim()) typeErrors.recipientName = true;
            if (!d.phoneNumber || !d.phoneNumber.trim()) typeErrors.phoneNumber = true;
            if (!d.address || !d.address.trim()) typeErrors.address = true;
            if (!d.postalCode || !d.postalCode.trim()) typeErrors.postalCode = true;
            if (!d.municipality || !d.municipality.trim()) typeErrors.municipality = true;
            if (!d.colonia || !d.colonia.trim()) typeErrors.colonia = true;
            // tentativeDate removed — MANUS coordina la fecha directamente con el cliente
            // if (!d.tentativeDate) typeErrors.tentativeDate = true;

            if (Object.keys(typeErrors).length > 0) {
                newErrors[type] = typeErrors;
                hasErrors = true;
            }
        });

        if (hasErrors) {
            setValidationErrors(newErrors);
            toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Por favor completa todos los campos obligatorios para las visitas seleccionadas.' });
            return false;
        }
        return true;
    };

    const handleNext = () => {
        if (activeIndex === 0) {
            if (!validateVisits()) return;
            // Validate outcome is selected when status is 'completed'
            if (currentStatus === 'completed' && !completionOutcome) {
                toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Debes seleccionar si la visita fue Exitosa o No exitosa.' });
                return;
            }
        }

        if (skipCompanyEmail) {
            // 2-step wizard: Step 0 (Details) -> Step 1 (User Email, rendered at activeIndex=2 position)
            if (activeIndex === 0) {
                navigate(2);
                return;
            }
            if (activeIndex === 2) {
                handleSave();
                return;
            }
        } else {
            // 3-step wizard: normal flow
            if (activeIndex === 1) {
                const companyErrors = {};
                if (!companyRecipientName) companyErrors.companyRecipientName = true;
                if (!companyEmail) companyErrors.companyEmail = true;
                if (!companyEmailSubject) companyErrors.companyEmailSubject = true;
                if (!companyEmailContent) companyErrors.companyEmailContent = true;

                if (Object.keys(companyErrors).length > 0) {
                    setValidationErrors(prev => ({ ...prev, companyStep: companyErrors }));
                    toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Por favor complete los campos obligatorios del correo a la empresa.' });
                    return;
                }
                navigate(2);
                return;
            }

            if (activeIndex === 2) {
                handleSave();
                return;
            }
        }

        navigate(activeIndex + 1);
    };

    const handleBack = () => {
        if (skipCompanyEmail && activeIndex === 2) {
            navigate(0);
            return;
        }
        navigate(activeIndex - 1);
    };

    const handleSave = async () => {
        if (!processId && !stepData?.budget_id && !persona?.infoBudget?.budget_id) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se encontró el ID del proceso ni el presupuesto.' });
            return;
        }

        if (!validateVisits()) return;

        if (!email) {
            setValidationErrors(prev => ({ ...prev, emailStep: { ...(prev.emailStep || {}), email: true } }));
            toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'El correo del destinatario es obligatorio.' });
            return;
        }

        if (currentStatus !== 'pending') {
            const emailStepErrors = {};
            if (!emailSubject) emailStepErrors.emailSubject = true;
            if (!emailContent) emailStepErrors.emailContent = true;

            if (Object.keys(emailStepErrors).length > 0) {
                setValidationErrors(prev => ({ ...prev, emailStep: { ...(prev.emailStep || {}), ...emailStepErrors } }));
                toast.current?.show({ severity: 'warn', summary: 'Atención', detail: 'Por favor complete los campos obligatorios del correo.' });
                return;
            }
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const validCc = ccEmails.filter(c => c && c.trim() !== '');

            const formattedVisits = {};
            (selectedVisitTypes || []).forEach(type => {
                const d = { ...(visitsData[type] || {}) };
                // tentativeDate removed — no se envía al backend
                // if (d.tentativeDate) d.tentativeDate = d.tentativeDate.toISOString();
                delete d.coloniasOptions;
                formattedVisits[type] = d;
            });

            const formData = new FormData();
            formData.append('process_id', processId || '');

            Object.keys(formattedVisits || {}).forEach(type => {
                const visit = formattedVisits[type];
                Object.keys(visit || {}).forEach(field => {
                    const val = visit[field];
                    if (val !== null && val !== undefined) {
                        formData.append(`visit_data[${type}][${field}]`, val);
                    }
                });
            });

            formData.append('status', resolvedStatus);
            formData.append('admin_comment', adminComment);
            formData.append('email', email);

            (validCc || []).forEach((cc, index) => {
                formData.append(`cc_emails[${index}]`, cc);
            });

            if (emailContent) formData.append('email_content', emailContent);
            if (emailSubject) formData.append('email_subject', emailSubject);

            // Skip all company fields when the company email step is hidden (e.g. status === 'completed')
            // Backend uses $request->has() to preserve existing metadata when these fields are absent.
            if (!skipCompanyEmail) {
                formData.append('company_recipient_name', companyRecipientName);
                formData.append('company_email', companyEmail);
                (companyCcEmails || []).filter(c => c && c.trim() !== '').forEach((cc, index) => {
                    formData.append(`company_cc_emails[${index}]`, cc);
                });
                if (companyEmailSubject) formData.append('company_email_subject', companyEmailSubject);
                if (companyEmailContent) formData.append('company_email_content', companyEmailContent);
                (companyUploadedFiles || []).forEach((file, index) => {
                    formData.append(`company_attachments[${index}]`, file);
                });
                (excludedDefaultPaths || []).forEach((p, index) => {
                    formData.append(`excluded_default_attachments[${index}]`, p);
                });
            }

            formData.append('type_persona', isPM ? 'PM' : 'PF');

            if (!processId) {
                const budId = stepData?.budget_id || persona?.infoBudget?.budget_id;
                const perId = persona?.id;
                if (budId) formData.append('budget_id', budId);
                if (perId) formData.append('person_id', perId);
            }

            (uploadedFiles || []).forEach((file, index) => {
                formData.append(`attachments[${index}]`, file);
            });

            const response = await axios.post(`${url}/quote-process/ocular-visit`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                toast.current?.show({ severity: 'success', summary: 'Guardado', detail: 'Información actualizada y notificada.' });
                if (onSuccess) onSuccess(response.data.data);
                navigate(0);
            }
        } catch (error) {
            console.error('Error saving ocular visit:', error);
            const msg = error.response?.data?.message || 'Error al guardar la información';
            toast.current?.show({ severity: 'error', summary: 'Error', detail: msg });
        } finally {
            setLoading(false);
        }
    };

    // Only truly completed (successful) is read-only. Unsuccessful remains editable.
    const isReadOnly = stepData?.status === 'completed';

    // Si la visita fue guardada como "No exitosa", deshabilitamos el botón hasta que
    // el admin cambie el resultado (clic en Exitosa) o el estatus (a Programada/Reprogramada/Pausada).
    const isStuckOnUnsuccessful = stepData?.status === 'unsuccessful' && resolvedStatus === 'unsuccessful';

    const hasStep0Error = validationErrors.selectedVisitTypes || (selectedVisitTypes || []).some(type => validationErrors[type]);
    const hasStep1Error = validationErrors.companyStep && Object.keys(validationErrors.companyStep).length > 0;
    const hasStep2Error = validationErrors.emailStep && Object.keys(validationErrors.emailStep).length > 0;

    const items = skipCompanyEmail
        ? [
            { label: 'Detalles de Visita', className: hasStep0Error ? 'step-error' : '' },
            { label: 'Correo a Usuario', className: hasStep2Error ? 'step-error' : '' }
        ]
        : [
            { label: 'Detalles de Visita', className: hasStep0Error ? 'step-error' : '' },
            { label: 'Correo a Empresa', className: hasStep1Error ? 'step-error' : '' },
            { label: 'Correo a Usuario', className: hasStep2Error ? 'step-error' : '' }
        ];

    return (
        <div className="email-modal-content">
            <style>
                {`
                    .email-modal-content .p-inputtext,
                    .email-modal-content .p-inputtextarea {
                        width: 100%;
                    }

                    .email-modal-content .email-section {
                        margin-bottom: 1.5rem;
                    }

                    .email-modal-content label {
                        display: block;
                        margin-bottom: 0.5rem;
                        font-weight: 600;
                        color: #374151;
                    }

                    .email-modal-content .p-inputtextarea {
                        font-family: inherit;
                        line-height: 1.6;
                        min-height: 400px;
                    }

                    .email-modal-content .p-fileupload {
                        width: 100%;
                    }

                    .info-box {
                        background: #eff6ff;
                        border: 1px solid #bfdbfe;
                        border-radius: 8px;
                        padding: 1rem;
                        margin-bottom: 1.5rem;
                    }

                    .info-box i {
                        color: #2563eb;
                    }

                    .info-box p {
                        margin: 0;
                        color: #1e40af;
                        font-size: 0.875rem;
                    }

                    /* Step error styling */
                    .p-steps .p-steps-item.step-error .p-menuitem-link .p-steps-number {
                        background: #ef4444 !important;
                        color: #ffffff !important;
                        border-color: #ef4444 !important;
                    }
                    .p-steps .p-steps-item.step-error .p-menuitem-link .p-steps-title {
                        color: #ef4444 !important;
                        font-weight: 700 !important;
                    }
                `}
            </style>

            {/* Animación pulsante para el badge urgente y la etapa siguiente del tracker */}
            <style>{`
                @keyframes ocularPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.06); opacity: 0.92; }
                }
                @keyframes ocularDotPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55); }
                    70%      { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
                }
            `}</style>

            {/* Título + Steps compactos en la misma fila */}
            <div className="flex align-items-center justify-content-between gap-4 mb-3 flex-wrap">
                <div className="flex align-items-center gap-3" style={{ flexShrink: 0 }}>
                    <h2
                        style={{
                            background: "linear-gradient(90deg, #21588C 6.22%, #21588C 77.34%)",
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            margin: 0,
                            fontSize: '1.1rem',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Gestión de Visita Ocular
                    </h2>

                    <motion.div
                        key={`${currentStatus}-${completionOutcome ?? 'none'}-${badgeDetails.agingTier}`}
                        initial={{ opacity: 0, scale: 0.92, y: -2 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.45rem',
                            padding: '0.32rem 0.7rem 0.32rem 0.58rem',
                            borderRadius: '999px',
                            background: badgeDetails.bg,
                            border: `1px solid ${badgeDetails.border}`,
                            color: badgeDetails.text,
                            fontSize: '0.74rem',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            lineHeight: 1,
                            boxShadow: `0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px ${badgeDetails.bg}`,
                            whiteSpace: 'nowrap',
                            animation: badgeDetails.agingTier === 'urgent' ? 'ocularPulse 1.6s ease-in-out infinite' : undefined,
                        }}
                        title={`Estado actual: ${badgeDetails.label}${daysSinceUpdate !== null ? ` · hace ${daysSinceUpdate} día${daysSinceUpdate === 1 ? '' : 's'}` : ''}`}
                    >
                        <span
                            style={{
                                width: '7px',
                                height: '7px',
                                borderRadius: '50%',
                                background: badgeDetails.dot,
                                boxShadow: `0 0 0 3px ${badgeDetails.bg}, 0 0 6px ${badgeDetails.shadow}`,
                                flexShrink: 0,
                            }}
                        />
                        <i className={badgeDetails.icon} style={{ fontSize: '0.78rem' }} />
                        <span>{badgeDetails.label}</span>
                        {badgeDetails.agingTier !== 'fresh' && daysSinceUpdate !== null && (
                            <span style={{
                                marginLeft: '0.1rem',
                                padding: '0.05rem 0.35rem',
                                borderRadius: '99px',
                                background: badgeDetails.text,
                                color: '#fff',
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                            }}>
                                {daysSinceUpdate}d
                            </span>
                        )}
                    </motion.div>
                </div>

                <div className="flex align-items-center gap-2">
                    {items.map((item, idx) => {
                        const displayedActive = skipCompanyEmail ? (activeIndex === 0 ? 0 : 1) : activeIndex;
                        const isActive = idx === displayedActive;
                        const isError = item.className === 'step-error';
                        const circleBg = isError ? '#ef4444' : (isActive ? '#00BA6E' : '#e2e8f0');
                        const circleColor = isError || isActive ? '#fff' : '#64748b';
                        const labelColor = isError ? '#ef4444' : (isActive ? '#00BA6E' : '#64748b');
                        const handleClick = () => {
                            if (skipCompanyEmail) {
                                navigate(idx === 0 ? 0 : 2);
                            } else {
                                navigate(idx);
                            }
                        };
                        return (
                            <React.Fragment key={idx}>
                                <div
                                    onClick={handleClick}
                                    className="flex flex-column align-items-center"
                                    style={{
                                        gap: '0.2rem',
                                        cursor: 'pointer',
                                        opacity: isActive || isError ? 1 : 0.7,
                                        transition: 'opacity 0.15s',
                                    }}
                                >
                                    <div style={{
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        background: circleBg,
                                        color: circleColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        boxShadow: isActive ? '0 1px 3px rgba(0,186,110,0.3)' : 'none',
                                    }}>
                                        {idx + 1}
                                    </div>
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: labelColor,
                                        whiteSpace: 'nowrap',
                                        letterSpacing: '0.02em',
                                    }}>
                                        {item.label}
                                    </span>
                                </div>
                                {idx < items.length - 1 && (
                                    <div style={{
                                        width: '20px',
                                        height: '1px',
                                        background: '#cbd5e1',
                                        marginBottom: '0.9rem',
                                        flexShrink: 0,
                                    }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>


            {/* Tracker de progreso + Sub-leyenda de "siguiente paso" */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.9rem',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    padding: '0.55rem 0.85rem',
                    marginBottom: '0.85rem',
                    flexWrap: 'wrap',
                }}
            >
                {/* Mini tracker */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                    {progressTracker.stages.map((stage, idx) => {
                        const isDone = idx < progressTracker.activeIdx;
                        const isCurrent = idx === progressTracker.activeIdx;
                        const isLastStageFailure = idx === 2 && progressTracker.lastStageVariant === 'failure';

                        let dotBg, dotColor, ringColor, labelColor;
                        if (isCurrent) {
                            dotBg = isLastStageFailure ? '#dc2626' : (idx === 2 ? '#16a34a' : '#2563eb');
                            dotColor = '#fff';
                            ringColor = isLastStageFailure ? '#fecaca' : (idx === 2 ? '#bbf7d0' : '#bfdbfe');
                            labelColor = isLastStageFailure ? '#b91c1c' : (idx === 2 ? '#15803d' : '#1d4ed8');
                        } else if (isDone) {
                            dotBg = '#16a34a';
                            dotColor = '#fff';
                            ringColor = '#dcfce7';
                            labelColor = '#15803d';
                        } else {
                            dotBg = '#e2e8f0';
                            dotColor = '#94a3b8';
                            ringColor = 'transparent';
                            labelColor = '#94a3b8';
                        }

                        return (
                            <React.Fragment key={stage.key}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <div
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: '50%',
                                            background: dotBg,
                                            color: dotColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.62rem',
                                            fontWeight: 700,
                                            boxShadow: ringColor !== 'transparent' ? `0 0 0 3px ${ringColor}` : 'none',
                                            animation: isCurrent && !isLastStageFailure && idx < 2 ? 'ocularDotPulse 1.8s ease-in-out infinite' : undefined,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isDone ? <i className="pi pi-check" style={{ fontSize: '0.55rem' }} /> : (idx + 1)}
                                    </div>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: labelColor, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
                                        {stage.label}
                                    </span>
                                </div>
                                {idx < progressTracker.stages.length - 1 && (
                                    <div
                                        style={{
                                            width: '24px',
                                            height: '2px',
                                            background: isDone ? '#86efac' : '#e2e8f0',
                                            borderRadius: '99px',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Sub-leyenda */}
                {nextStepHint && (
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        color: badgeDetails.agingTier === 'urgent' ? '#b91c1c'
                            : badgeDetails.agingTier === 'warning' ? '#b45309'
                            : '#475569',
                        fontSize: '0.72rem',
                        lineHeight: 1.3,
                        flex: 1,
                        minWidth: 0,
                    }}>
                        <i className={
                            badgeDetails.agingTier === 'urgent' ? 'pi pi-exclamation-circle'
                            : badgeDetails.agingTier === 'warning' ? 'pi pi-exclamation-triangle'
                            : 'pi pi-arrow-right'
                        } style={{ fontSize: '0.75rem', flexShrink: 0 }} />
                        <span>{nextStepHint}</span>
                    </div>
                )}
            </div>

            {/* STEPS */}
            <AnimatePresence mode="wait" custom={direction}>
            <motion.div
                key={activeIndex}
                custom={direction}
                initial={(dir) => ({ opacity: 0, x: dir * 28 })}
                animate={{ opacity: 1, x: 0 }}
                exit={(dir) => ({ opacity: 0, x: -dir * 28 })}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            >

            {/* STEP 1: VISIT DETAILS */}
            {activeIndex === 0 && <div>
                <StepVisitDetails
                    currentStatus={currentStatus}
                    handleStatusChange={handleStatusChange}
                    statusOptions={statusOptions}
                    isReadOnly={isReadOnly}
                    isPM={isPM}
                    selectedVisitTypes={selectedVisitTypes}
                    visitTypeOptions={visitTypeOptions}
                    handleVisitTypeChange={handleVisitTypeChange}
                    visitsData={visitsData}
                    updateVisitData={updateVisitData}
                    validationErrors={validationErrors}
                    completionOutcome={completionOutcome}
                    onCompletionOutcomeChange={handleCompletionOutcomeChange}
                />
            </div>}

            {/* STEP 2: COMPANY EMAIL (hidden in 2-step mode) */}
            {activeIndex === 1 && !skipCompanyEmail && <div>
                <StepCompanyEmail
                    currentStatus={currentStatus}
                    companyRecipientName={companyRecipientName}
                    setCompanyRecipientName={(val) => {
                        setCompanyRecipientName(val);
                        // Auto-regenerate content when name changes
                        setCompanyEmailContent(generateCompanyEmailContent(currentStatus, visitsData, selectedVisitTypes, persona, isPM, val));

                        if (val && validationErrors.companyStep?.companyRecipientName) {
                            setValidationErrors(prev => {
                                const newStep = { ...prev.companyStep };
                                delete newStep.companyRecipientName;
                                return { ...prev, companyStep: newStep };
                            });
                        }
                    }}
                    companyEmail={companyEmail}
                    setCompanyEmail={(val) => {
                        setCompanyEmail(val);
                        if (val && validationErrors.companyStep?.companyEmail) {
                            setValidationErrors(prev => {
                                const newStep = { ...prev.companyStep };
                                delete newStep.companyEmail;
                                return { ...prev, companyStep: newStep };
                            });
                        }
                    }}
                    companyCcEmails={companyCcEmails}
                    setCompanyCcEmails={setCompanyCcEmails}
                    companyEmailSubject={companyEmailSubject}
                    setCompanyEmailSubject={(val) => {
                        setCompanyEmailSubject(val);
                        if (val && validationErrors.companyStep?.companyEmailSubject) {
                            setValidationErrors(prev => {
                                const newStep = { ...prev.companyStep };
                                delete newStep.companyEmailSubject;
                                return { ...prev, companyStep: newStep };
                            });
                        }
                    }}
                    companyEmailContent={companyEmailContent}
                    setCompanyEmailContent={(val) => {
                        setCompanyEmailContent(val);
                        if (val && validationErrors.companyStep?.companyEmailContent) {
                            setValidationErrors(prev => {
                                const newStep = { ...prev.companyStep };
                                delete newStep.companyEmailContent;
                                return { ...prev, companyStep: newStep };
                            });
                        }
                    }}
                    isReadOnly={isReadOnly}
                    setCompanyUploadedFiles={setCompanyUploadedFiles}
                    errors={validationErrors.companyStep || {}}
                    stepData={stepData}
                    defaultAttachments={defaultAttachments}
                    excludedDefaultPaths={excludedDefaultPaths}
                    onToggleDefaultPath={(s3Path) => {
                        setExcludedDefaultPaths((prev) => prev.includes(s3Path)
                            ? prev.filter((p) => p !== s3Path)
                            : [...prev, s3Path]);
                    }}
                />
            </div>}

            {/* STEP 3: USER EMAIL */}
            {activeIndex === 2 && <div>
                <StepUserEmail
                    email={email}
                    setEmail={(val) => {
                        setEmail(val);
                        if (val && validationErrors.emailStep?.email) {
                            setValidationErrors(prev => {
                                const newEmailStep = { ...prev.emailStep };
                                delete newEmailStep.email;
                                return { ...prev, emailStep: newEmailStep };
                            });
                        }
                    }}
                    ccEmails={ccEmails}
                    addCcEmail={addCcEmail}
                    updateCcEmail={updateCcEmail}
                    removeCcEmail={removeCcEmail}
                    currentStatus={currentStatus}
                    emailSubject={emailSubject}
                    setEmailSubject={(val) => {
                        setEmailSubject(val);
                        if (val && validationErrors.emailStep?.emailSubject) {
                            setValidationErrors(prev => {
                                const newEmailStep = { ...prev.emailStep };
                                delete newEmailStep.emailSubject;
                                return { ...prev, emailStep: newEmailStep };
                            });
                        }
                    }}
                    emailContent={emailContent}
                    setEmailContent={(val) => {
                        setEmailContent(val);
                        if (val && validationErrors.emailStep?.emailContent) {
                            setValidationErrors(prev => {
                                const newEmailStep = { ...prev.emailStep };
                                delete newEmailStep.emailContent;
                                return { ...prev, emailStep: newEmailStep };
                            });
                        }
                    }}
                    adminComment={adminComment}
                    setAdminComment={setAdminComment}
                    stepData={stepData}
                    setUploadedFiles={setUploadedFiles}
                    isReadOnly={isReadOnly}
                    errors={validationErrors.emailStep || {}}
                    resolvedStatus={resolvedStatus}
                />
            </div>}

            </motion.div>
            </AnimatePresence>

            <div className="flex justify-content-end gap-2 mt-4 pt-3 border-top-1 border-gray-200">
                {!isReadOnly && activeIndex > 0 && (
                    <Button
                        label="Atrás"
                        icon="pi pi-chevron-left"
                        onClick={handleBack}
                        className="p-button-outlined p-button-secondary"
                    />
                )}

                {!isReadOnly && (
                    <Button
                        label={
                            (skipCompanyEmail && activeIndex === 2) || (!skipCompanyEmail && activeIndex === 2)
                                ? (isCompleted && currentStatus === 'completed' ? "Actualizar Datos" : "Guardar y Notificar")
                                : "Siguiente"
                        }
                        icon={
                            (skipCompanyEmail && activeIndex === 2) || (!skipCompanyEmail && activeIndex === 2)
                                ? "pi pi-save" : "pi pi-chevron-right"
                        }
                        iconPos={
                            (skipCompanyEmail && activeIndex === 2) || (!skipCompanyEmail && activeIndex === 2)
                                ? "left" : "right"
                        }
                        onClick={handleNext}
                        loading={loading}
                        disabled={(activeIndex === 0 && currentStatus === 'pending') || isStuckOnUnsuccessful}
                        tooltip={isStuckOnUnsuccessful ? 'Cambia el estatus o marca el resultado como Exitosa para volver a guardar.' : undefined}
                        tooltipOptions={{ position: 'top' }}
                        className="Button Green"
                    />
                )}
            </div>
        </div>
    );
};
